import * as http from "http";
import * as https from "https";

import bodyParser from "body-parser";
import cookieParser from "cookie-parser";
import express, { Express } from "express";
import { readFile } from "fs-extra";

import { ILogger } from "../../Logging/ILogger";
import { httpSetupAttributeRouting } from "../AttributeRouting/Methods";
import { reject } from "../../../../../Common/TypingUtils";

import { SingletonController } from "./SingletonController";

export class ExpressServerWrapper {
    private readonly expressApplication: Express;
    private readonly port: number;
    private readonly httpsPort: undefined | number;
    private readonly singletonControllers: SingletonController[] = [];
    private readonly logger: ILogger;
    private readonly name: undefined | string;
    private httpServer?: http.Server;
    private httpsServer?: undefined | https.Server;

    public constructor(port: number, httpsPort: undefined | number, logger: ILogger, name?: string) {
        this.name = name;
        this.port = port;
        this.httpsPort = httpsPort;
        this.logger = logger;
        this.expressApplication = express();
        this.expressApplication.use(cookieParser());
        this.expressApplication.use(bodyParser.json({ limit: "50mb" }));
        this.expressApplication.use(bodyParser.raw({ type: "application/x-www-form-urlencoded" }));
        this.expressApplication.use(bodyParser.raw({ type: "application/octet-stream" }));
        this.expressApplication.use((req, _, next) => {
            this.logger.verbose(req.url);
            next();
        });
        this.configureApiRoutes();
    }

    public registerSigletonController(controller: SingletonController): void {
        this.singletonControllers.push(controller);
    }

    public async start(): Promise<void> {
        for (const controller of this.singletonControllers) {
            await controller.setupMiddleware(this.expressApplication);
        }
        for (const controller of this.singletonControllers) {
            httpSetupAttributeRouting(controller, this.expressApplication);
        }
        for (const controller of this.singletonControllers) {
            await controller.beforeStart();
        }
        await this.startExpressApplication();
        for (const controller of this.singletonControllers) {
            await controller.afterStart(this.httpServer ?? reject(), this.httpsServer);
        }
        // eslint-disable-next-line no-console
        console.log(`${this.name ?? "Server"} started at port ${this.port}!`);
        if (this.httpsPort != undefined) {
            // eslint-disable-next-line no-console
            console.log(`Also ${this.name ?? "Server"} started at https on port ${this.httpsPort}!`);
        }
    }

    public async stop(): Promise<void> {
        this.httpServer?.close();
        this.httpsServer?.close();
        for (const controller of this.singletonControllers) {
            await controller.afterStop();
        }
    }

    private async startExpressApplication(): Promise<void> {
        this.httpServer = http.createServer(this.expressApplication);
        this.httpsServer =
            this.httpsPort != undefined
                ? https.createServer(
                      {
                          cert: await readFile(require.resolve("../../../Keys/localhost.pem")),
                          key: await readFile(require.resolve("../../../Keys/localhost-key.pem")),
                      },
                      this.expressApplication
                  )
                : undefined;
        await Promise.all([
            new Promise<void>(resolve => {
                this.httpServer?.listen(this.port, async () => setTimeout(() => resolve(), 1));
            }),
            new Promise<void>(resolve => {
                if (this.httpsServer != undefined) {
                    this.httpsServer.listen(this.httpsPort, async () => setTimeout(() => resolve(), 1));
                } else {
                    resolve();
                }
            }),
        ]);
    }

    private configureApiRoutes(): void {
        // virtual
    }
}
